import React from 'react';
import { displayJobLocation, isArabicMoreInString, convertArabicNumbersToEnglish } from './utils';
import { whatsappTemplatePlaceHolders, TemplatePlaceholderEnum, WhatsAppTemplate } from '../interfaces/whatsapp/WhatsappTemplatesTypes';
import { FullTimeJobDetails } from '../interfaces/job/JobDetails';
import { Partner, PartnerUser } from '../interfaces/partner/PartnerProfile';
import { ApplicantListItem } from '../interfaces/applicants/ApplicantListItem';
import { t } from "i18next";

export interface TemplateButton {
    type: 'flow' | 'url' | 'quick_reply';
    text: string;
    url?: string;
    flow_id?: string;
    flow_action?: string;
}

/**
 * Protects placeholders ({{...}}) from formatting by temporarily replacing them
 * @param text - The text containing placeholders
 * @returns Object containing the protected text and a map to restore placeholders
 */
export const protectPlaceholders = (text: string): { protectedText: string; placeholderMap: Map<string, string> } => {
    const placeholderMap: Map<string, string> = new Map();
    let placeholderCounter = 0;
    const placeholderRegex = /\{\{[^}]+\}\}/g;
    const protectedText = text.replace(placeholderRegex, (match) => {
        const placeholderKey = `PLACEHOLDERX${placeholderCounter}X`;
        placeholderMap.set(placeholderKey, match);
        placeholderCounter++;
        return placeholderKey;
    });
    return { protectedText, placeholderMap };
};

/**
 * Restores placeholders in text using the placeholder map
 * @param text - The text with placeholder keys
 * @param placeholderMap - Map of placeholder keys to original values
 * @returns Text with placeholders restored
 */
export const restorePlaceholders = (text: string, placeholderMap: Map<string, string>): string => {
    let restored = text;
    placeholderMap.forEach((original, key) => {
        restored = restored.replace(key, original);
    });
    return restored;
};

/**
 * Gets the placeholder display format based on language
 * @param placeholderId - The placeholder enum ID
 * @param lang - The language ('en' or 'ar')
 * @returns The placeholder in display format (e.g., {{candidate_name}} or {{اسم_المرشح}})
 */
export const getPlaceholderDisplayFormat = (placeholderId: TemplatePlaceholderEnum, lang: 'en' | 'ar'): string => {
    const placeholder = whatsappTemplatePlaceHolders.find(p => p.id === placeholderId);
    if (!placeholder) return '';

    const content = placeholder.content[lang];
    if (!content) return '';

    // Replace spaces with underscores, convert to lowercase for English, and wrap in double curly braces
    let formatted = content.replace(/ /g, '_');
    if (lang === 'en') {
        formatted = formatted.toLowerCase();
    }
    return `{{${formatted}}}`;
};

/**
 * Converts template placeholders to language-specific format
 * @param text - The template text with placeholders
 * @param lang - The target language ('en' or 'ar')
 * @returns Text with placeholders converted to language-specific format
 */
export const convertPlaceholdersToLanguage = (text: string, lang: 'en' | 'ar'): string => {
    if (!text) return text;

    let convertedText = text;

    // Replace all placeholder formats (API format, old formats, other language formats, square brackets) with current language format
    for (const placeholder of whatsappTemplatePlaceHolders) {
        const apiFormat = `{${placeholder.id}}`;
        const targetFormat = getPlaceholderDisplayFormat(placeholder.id, lang);

        // Also handle old formats
        const oldFormatEn = getPlaceholderDisplayFormat(placeholder.id, 'en');
        const oldFormatAr = getPlaceholderDisplayFormat(placeholder.id, 'ar');

        // Handle square bracket format [placeholder] and convert to {{placeholder}}
        const squareBracketFormatEn = `[${placeholder.content.en.replace(/ /g, '_').toLowerCase()}]`;
        const squareBracketFormatAr = `[${placeholder.content.ar.replace(/ /g, '_')}]`;

        // Replace API format
        convertedText = convertedText.replace(new RegExp(apiFormat.replace(/[{}]/g, '\\$&'), 'g'), targetFormat);

        // Replace square bracket formats with curly brace format
        convertedText = convertedText.replace(new RegExp(squareBracketFormatEn.replace(/[\[\]]/g, '\\$&'), 'g'), targetFormat);
        convertedText = convertedText.replace(new RegExp(squareBracketFormatAr.replace(/[\[\]]/g, '\\$&'), 'g'), targetFormat);

        // Replace other language format with current language format
        // Escape special regex characters in the placeholder formats
        // Also handle case-insensitive matching since templates might have lowercase placeholders
        if (lang === 'ar') {
            // Escape curly braces and other special characters for regex
            const escapedEnFormat = oldFormatEn.replace(/[{}[\]()*+?.\\^$|]/g, '\\$&');
            // Make it case-insensitive to match both {{candidate_name}} and {{Candidate_Name}}
            convertedText = convertedText.replace(new RegExp(escapedEnFormat, 'gi'), targetFormat);
        } else {
            // Escape curly braces and other special characters for regex
            const escapedArFormat = oldFormatAr.replace(/[{}[\]()*+?.\\^$|]/g, '\\$&');
            // Make it case-insensitive to match both {{اسم_المرشح}} and variations
            convertedText = convertedText.replace(new RegExp(escapedArFormat, 'gi'), targetFormat);
        }
    }

    return convertedText;
};

/**
 * Replaces template placeholders with actual values or keeps placeholders based on context
 * @param text - The template text with placeholders
 * @param lang - The current language ('en' | 'ar')
 * @param isMultipleApplicants - Whether multiple applicants are selected (if true, keep placeholders)
 * @param placeholderValues - Object containing actual values for placeholders
 * @returns The text with placeholders replaced or kept based on context
 */
export const replaceTemplatePlaceholders = (
    text: string,
    lang: 'en' | 'ar',
    isMultipleApplicants: boolean,
    placeholderValues: {
        candidateName?: string;
        jobTitle?: string;
        companyName?: string;
        recruiterName?: string;
        jobLocation?: string;
        contractType?: string;
        interviewInvitationLink?: string;
        interviewInvitationSelectedSlot?: string;
    }
): string => {
    if (!text) return text;

    // First, convert all placeholders to language-specific format
    let replacedText = convertPlaceholdersToLanguage(text, lang);

    // Replace each placeholder
    for (const placeholder of whatsappTemplatePlaceHolders) {
        const apiFormat = `{${placeholder.id}}`;
        const displayFormat = getPlaceholderDisplayFormat(placeholder.id, lang);

        // Also handle old formats from other languages
        const displayFormatEn = getPlaceholderDisplayFormat(placeholder.id, 'en');
        const displayFormatAr = getPlaceholderDisplayFormat(placeholder.id, 'ar');

        let replacementValue: string;

        // Determine if we should show placeholder format instead of actual value
        let shouldShowPlaceholder = false;

        if (isMultipleApplicants) {
            // Use placeholder format when multiple applicants
            shouldShowPlaceholder = true;
        } else {
            // Check if actual value is missing
            switch (placeholder.id) {
                case TemplatePlaceholderEnum.CANDIDATE_NAME:
                    shouldShowPlaceholder = !placeholderValues.candidateName;
                    break;
                case TemplatePlaceholderEnum.JOB_TITLE:
                    shouldShowPlaceholder = !placeholderValues.jobTitle;
                    break;
                case TemplatePlaceholderEnum.COMPANY_NAME:
                    shouldShowPlaceholder = !placeholderValues.companyName;
                    break;
                case TemplatePlaceholderEnum.RECRUITER_NAME:
                    shouldShowPlaceholder = !placeholderValues.recruiterName;
                    break;
                case TemplatePlaceholderEnum.JOB_LOCATION:
                    shouldShowPlaceholder = !placeholderValues.jobLocation;
                    break;
                case TemplatePlaceholderEnum.CONTRACT_TYPE:
                    shouldShowPlaceholder = !placeholderValues.contractType;
                    break;
                case TemplatePlaceholderEnum.INTERVIEW_INVITATION_LINK:
                    shouldShowPlaceholder = !placeholderValues.interviewInvitationLink;
                    break;
                case TemplatePlaceholderEnum.INTERVIEW_INVITATION_SELECTED_SLOT:
                    shouldShowPlaceholder = !placeholderValues.interviewInvitationSelectedSlot;
                    break;
                default:
                    shouldShowPlaceholder = false;
            }
        }

        if (shouldShowPlaceholder) {
            // Use placeholder format with brackets (e.g., {{candidate_name}} or {{اسم_المرشح}})
            replacementValue = displayFormat;
        } else {
            // Use actual value if available
            switch (placeholder.id) {
                case TemplatePlaceholderEnum.CANDIDATE_NAME:
                    replacementValue = placeholderValues.candidateName || displayFormat;
                    break;
                case TemplatePlaceholderEnum.JOB_TITLE:
                    replacementValue = placeholderValues.jobTitle || displayFormat;
                    break;
                case TemplatePlaceholderEnum.COMPANY_NAME:
                    replacementValue = placeholderValues.companyName || displayFormat;
                    break;
                case TemplatePlaceholderEnum.RECRUITER_NAME:
                    replacementValue = placeholderValues.recruiterName || displayFormat;
                    break;
                case TemplatePlaceholderEnum.JOB_LOCATION:
                    replacementValue = placeholderValues.jobLocation || displayFormat;
                    break;
                case TemplatePlaceholderEnum.CONTRACT_TYPE:
                    replacementValue = placeholderValues.contractType || displayFormat;
                    break;
                case TemplatePlaceholderEnum.INTERVIEW_INVITATION_LINK:
                    replacementValue = placeholderValues.interviewInvitationLink || displayFormat;
                    break;
                case TemplatePlaceholderEnum.INTERVIEW_INVITATION_SELECTED_SLOT:
                    replacementValue = placeholderValues.interviewInvitationSelectedSlot || displayFormat;
                    break;
                default:
                    replacementValue = displayFormat;
            }
        }

        // Handle square bracket format [placeholder] and convert to {{placeholder}} first
        const squareBracketFormatEn = `[${placeholder.content.en.replace(/ /g, '_').toLowerCase()}]`;
        const squareBracketFormatAr = `[${placeholder.content.ar.replace(/ /g, '_')}]`;

        // Replace square bracket formats with curly brace format (case-insensitive for English)
        replacedText = replacedText.replace(new RegExp(squareBracketFormatEn.replace(/[\[\]]/g, '\\$&'), 'gi'), replacementValue);
        replacedText = replacedText.replace(new RegExp(squareBracketFormatAr.replace(/[\[\]]/g, '\\$&'), 'g'), replacementValue);

        // Replace placeholders - prioritize current language format first
        // This ensures Arabic placeholders are matched before English when language is Arabic
        // Use case-insensitive matching to handle variations like {{candidate_name}} vs {{Candidate_Name}}
        if (lang === 'ar') {
            // For Arabic: match Arabic format first, then English, then API format
            replacedText = replacedText.replace(new RegExp(displayFormatAr.replace(/[{}]/g, '\\$&'), 'gi'), replacementValue);
            replacedText = replacedText.replace(new RegExp(displayFormatEn.replace(/[{}]/g, '\\$&'), 'gi'), replacementValue);
            replacedText = replacedText.replace(new RegExp(apiFormat.replace(/[{}]/g, '\\$&'), 'gi'), replacementValue);
        } else {
            // For English: match English format first, then Arabic, then API format
            replacedText = replacedText.replace(new RegExp(displayFormatEn.replace(/[{}]/g, '\\$&'), 'gi'), replacementValue);
            replacedText = replacedText.replace(new RegExp(displayFormatAr.replace(/[{}]/g, '\\$&'), 'gi'), replacementValue);
            replacedText = replacedText.replace(new RegExp(apiFormat.replace(/[{}]/g, '\\$&'), 'gi'), replacementValue);
        }
    }

    return replacedText;
};

/**
 * Renders text with placeholders as React elements (with bold styling)
 * @param text - The text containing placeholder keys
 * @param placeholderMap - Map of placeholder keys to original values
 * @param startKey - Starting key index for React keys
 * @param lang - The current language for placeholder display
 * @returns Object containing React elements and the next key index
 */
export const renderTextWithPlaceholders = (
    text: string,
    placeholderMap: Map<string, string>,
    startKey: number,
    lang: 'en' | 'ar' = 'en'
): { elements: React.ReactNode[]; nextKey: number } => {
    const elements: React.ReactNode[] = [];
    let key = startKey;
    const textParts = text.split(/(PLACEHOLDERX\d+X)/);

    textParts.forEach((part) => {
        if (part.match(/^PLACEHOLDERX\d+X$/)) {
            const original = placeholderMap.get(part);
            if (original) {
                // Extract placeholder name from {{placeholder_name}} format
                const placeholderText = original.replace(/\{\{|\}\}/g, '');

                // Find the placeholder to get the language-specific display name
                let displayName = placeholderText;
                for (const placeholder of whatsappTemplatePlaceHolders) {
                    const enFormat = placeholder.content.en.replace(/ /g, '_');
                    const arFormat = placeholder.content.ar.replace(/ /g, '_');
                    if (placeholderText.toLowerCase() === enFormat.toLowerCase() || placeholderText === arFormat) {
                        displayName = placeholder.content[lang];
                        break;
                    }
                }

                elements.push(
                    <strong key={`var-${key}`} className="template-variable">
                        {displayName}
                    </strong>
                );
                key++;
            }
        } else if (part) {
            elements.push(<span key={`text-${key}`}>{part}</span>);
            key++;
        }
    });

    return { elements, nextKey: key };
};

/**
 * Formats WhatsApp template content with bold, italic, and other formatting
 * while preserving placeholders
 * @param content - The template content string
 * @returns React element containing formatted content
 */
export const formatWhatsAppTemplateContent = (content: string, lang?: 'en' | 'ar'): React.ReactElement => {
    if (!content) return <div className="template-content-text"></div>;

    // Get language from content or parameter or default to 'en'
    const currentLang = lang || (isArabicMoreInString(content) ? 'ar' : 'en');

    // Convert placeholders to language-specific format first
    const contentWithLanguagePlaceholders = convertPlaceholdersToLanguage(content, currentLang);

    // Protect placeholders from formatting
    const { protectedText, placeholderMap } = protectPlaceholders(contentWithLanguagePlaceholders);

    // Split by WhatsApp formatting markers (bold, italic, strikethrough, code)
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let partKey = 0;
    const regex = /(\*[^*]+\*)|(_[^_]+_)|(~[^~]+~)|(```[^`]+```)/g;
    let match;

    while ((match = regex.exec(protectedText)) !== null) {
        // Add text before the match
        if (match.index > currentIndex) {
            const beforeText = protectedText.substring(currentIndex, match.index);
            const restoredBeforeText = restorePlaceholders(beforeText, placeholderMap);
            const { elements, nextKey } = renderTextWithPlaceholders(restoredBeforeText, placeholderMap, partKey, lang);
            parts.push(...elements);
            partKey = nextKey;
        }

        const matchedText = match[0];
        const innerText = matchedText.substring(1, matchedText.length - 1);

        // Apply formatting based on markers
        if (matchedText.startsWith('*')) {
            parts.push(<strong key={`format-${partKey}`}>{innerText}</strong>);
        } else if (matchedText.startsWith('_')) {
            parts.push(<em key={`format-${partKey}`}>{innerText}</em>);
        } else if (matchedText.startsWith('~')) {
            parts.push(<del key={`format-${partKey}`}>{innerText}</del>);
        } else if (matchedText.startsWith('```')) {
            parts.push(<code key={`format-${partKey}`}>{innerText}</code>);
        }
        partKey++;

        currentIndex = match.index + matchedText.length;
    }

    // Add remaining text
    if (currentIndex < protectedText.length) {
        const remainingText = protectedText.substring(currentIndex);
        const restoredRemainingText = restorePlaceholders(remainingText, placeholderMap);
        const { elements } = renderTextWithPlaceholders(restoredRemainingText, placeholderMap, partKey, lang);
        parts.push(...elements);
    }

    // If no formatting was found but placeholders exist, restore them
    if (parts.length === 0 && placeholderMap.size > 0) {
        const restoredText = restorePlaceholders(protectedText, placeholderMap);
        const textParts = restoredText.split(/(\{\{[^}]+\}\})/);
        const result = (
            <>
                {textParts.map((part, index) => {
                    if (part.match(/^\{\{[^}]+\}\}$/)) {
                        const placeholderText = part.replace(/\{\{|\}\}/g, '');

                        // Find the placeholder to get the language-specific display name
                        let displayName = placeholderText;
                        for (const placeholder of whatsappTemplatePlaceHolders) {
                            const enFormat = placeholder.content.en.replace(/ /g, '_');
                            const arFormat = placeholder.content.ar.replace(/ /g, '_');
                            if (placeholderText.toLowerCase() === enFormat.toLowerCase() || placeholderText === arFormat) {
                                displayName = placeholder.content[currentLang];
                                break;
                            }
                        }

                        return (
                            <strong key={index} className="template-variable">
                                {displayName}
                            </strong>
                        );
                    }
                    return <span key={index}>{part}</span>;
                })}
            </>
        );
        // Process newlines in the result
        const processedResult = processNewlinesInNodes(result);
        return (
            <div className={`template-content-text ${isArabicMoreInString(content) ? 'text-dir-right' : 'text-dir-left'}`}>
                {processedResult}
            </div>
        );
    }

    // Process newlines in the parts array
    const result = parts.length > 0 ? <>{parts}</> : content;
    const processedResult = processNewlinesInNodes(result);

    return (
        <div className={`template-content-text ${isArabicMoreInString(content) ? 'text-dir-right' : 'text-dir-left'}`}>
            {processedResult}
        </div>
    );
};

/**
 * Helper function to process React nodes and replace newlines with <br /> tags
 */
const processNewlinesInNodes = (nodes: React.ReactNode, keyPrefix: string = ''): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let keyIndex = 0;

    const processNode = (node: React.ReactNode): void => {
        if (typeof node === 'string') {
            // Split string by newlines and insert <br /> between parts
            const parts = node.split('\n');
            parts.forEach((part, index) => {
                if (part) {
                    result.push(<span key={`${keyPrefix}-text-${keyIndex++}`}>{part}</span>);
                }
                if (index < parts.length - 1) {
                    result.push(<br key={`${keyPrefix}-br-${keyIndex++}`} />);
                }
            });
        } else if (React.isValidElement(node)) {
            // For React elements, recursively process children
            const children = node.props.children;
            if (children != null) {
                const processedChildren = processNewlinesInNodes(children, `${keyPrefix}-${keyIndex}`);
                // Handle array children properly
                const childrenToPass = Array.isArray(processedChildren) && processedChildren.length === 1
                    ? processedChildren[0]
                    : processedChildren;
                result.push(React.cloneElement(node, { key: `${keyPrefix}-elem-${keyIndex++}` }, childrenToPass));
            } else {
                result.push(React.cloneElement(node, { key: `${keyPrefix}-elem-${keyIndex++}` }));
            }
        } else if (Array.isArray(node)) {
            // Process each node in the array
            node.forEach((item) => processNode(item));
        } else if (node != null && node !== false) {
            // For other types (numbers, etc.), convert to string and process
            const str = String(node);
            if (str.includes('\n')) {
                const parts = str.split('\n');
                parts.forEach((part, index) => {
                    if (part) {
                        result.push(<span key={`${keyPrefix}-other-${keyIndex++}`}>{part}</span>);
                    }
                    if (index < parts.length - 1) {
                        result.push(<br key={`${keyPrefix}-br-${keyIndex++}`} />);
                    }
                });
            } else {
                result.push(<span key={`${keyPrefix}-other-${keyIndex++}`}>{node}</span>);
            }
        }
    };

    if (Array.isArray(nodes)) {
        nodes.forEach((node) => processNode(node));
    } else {
        processNode(nodes);
    }

    return result;
};

/**
 * Formats WhatsApp template text with bold, italic, and other formatting
 * Returns React nodes (without wrapper div) for use in dropdowns or inline elements
 * @param content - The template content string
 * @param replaceNewlines - If true, replaces \n with <br /> tags (default: false)
 * @returns React nodes containing formatted content
 */
export const formatWhatsAppTemplateText = (content: string, replaceNewlines: boolean = false, lang?: 'en' | 'ar'): React.ReactNode => {
    if (!content) return content;

    // Get language from parameter or content or default to 'en'
    const currentLang = lang || (isArabicMoreInString(content) ? 'ar' : 'en');

    // Convert placeholders to language-specific format first
    let contentWithLanguagePlaceholders = convertPlaceholdersToLanguage(content, currentLang);

    // Ensure all placeholders are converted to Arabic if language is Arabic
    contentWithLanguagePlaceholders = replaceVariablesToArabic(contentWithLanguagePlaceholders, currentLang);

    // Protect placeholders from formatting
    const { protectedText, placeholderMap } = protectPlaceholders(contentWithLanguagePlaceholders);

    // Split by WhatsApp formatting markers (bold, italic, strikethrough, code)
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let partKey = 0;
    const regex = /(\*[^*]+\*)|(_[^_]+_)|(~[^~]+~)|(```[^`]+```)/g;
    let match;

    while ((match = regex.exec(protectedText)) !== null) {
        // Add text before the match
        if (match.index > currentIndex) {
            const beforeText = protectedText.substring(currentIndex, match.index);
            const restoredBeforeText = restorePlaceholders(beforeText, placeholderMap);
            const { elements, nextKey } = renderTextWithPlaceholders(restoredBeforeText, placeholderMap, partKey, currentLang);
            parts.push(...elements);
            partKey = nextKey;
        }

        const matchedText = match[0];
        const innerText = matchedText.substring(1, matchedText.length - 1);

        // Apply formatting based on markers
        if (matchedText.startsWith('*')) {
            parts.push(<strong key={`format-${partKey}`}>{innerText}</strong>);
        } else if (matchedText.startsWith('_')) {
            parts.push(<em key={`format-${partKey}`}>{innerText}</em>);
        } else if (matchedText.startsWith('~')) {
            parts.push(<del key={`format-${partKey}`}>{innerText}</del>);
        } else if (matchedText.startsWith('```')) {
            parts.push(<code key={`format-${partKey}`}>{innerText}</code>);
        }
        partKey++;

        currentIndex = match.index + matchedText.length;
    }

    // Add remaining text
    if (currentIndex < protectedText.length) {
        const remainingText = protectedText.substring(currentIndex);
        const restoredRemainingText = restorePlaceholders(remainingText, placeholderMap);
        const { elements } = renderTextWithPlaceholders(restoredRemainingText, placeholderMap, partKey, currentLang);
        parts.push(...elements);
    }

    // If no formatting was found but placeholders exist, restore them
    if (parts.length === 0 && placeholderMap.size > 0) {
        let restoredText = restorePlaceholders(protectedText, placeholderMap);
        // Ensure all placeholders are converted to Arabic if language is Arabic
        restoredText = replaceVariablesToArabic(restoredText, currentLang);
        const textParts = restoredText.split(/(\{\{[^}]+\}\})/);
        const result = (
            <>
                {textParts.map((part, index) => {
                    if (part.match(/^\{\{[^}]+\}\}$/)) {
                        const placeholderText = part.replace(/\{\{|\}\}/g, '');

                        // Find the placeholder to get the language-specific display name
                        let displayName = placeholderText;
                        for (const placeholder of whatsappTemplatePlaceHolders) {
                            const enFormat = placeholder.content.en.replace(/ /g, '_');
                            const arFormat = placeholder.content.ar.replace(/ /g, '_');
                            if (placeholderText.toLowerCase() === enFormat.toLowerCase() || placeholderText === arFormat) {
                                displayName = placeholder.content[currentLang];
                                break;
                            }
                        }

                        return (
                            <strong key={index} className="template-variable">
                                {displayName}
                            </strong>
                        );
                    }
                    return <span key={index}>{part}</span>;
                })}
            </>
        );
        return replaceNewlines ? <>{processNewlinesInNodes(result)}</> : result;
    }

    const result = parts.length > 0 ? <>{parts}</> : content;
    return replaceNewlines ? <>{processNewlinesInNodes(result)}</> : result;
};

export const replaceVariablesInTemplate = (template: string, jobDetails: FullTimeJobDetails, partner: Partner, applicants: ApplicantListItem[], partnerUser: PartnerUser) => {
    if (!template) return template;

    // Get current language
    const lang = localStorage.getItem('i18nextLng')?.includes('ar') ? 'ar' : 'en';

    // Determine if multiple applicants (if more than 1, keep placeholders)
    const isMultipleApplicants = applicants && applicants.length > 1;
    let jobLocation1 = undefined;
    if (jobDetails?.locationInfo?.localizedAddress || jobDetails?.locationInfo?.localizedCity || jobDetails?.locationInfo?.localizedDistrict) {
        jobLocation1 = displayJobLocation(jobDetails, lang);
    }


    // Prepare placeholder values
    const candidateName = applicants?.[0] ? `${applicants[0].firstName || ''} ${applicants[0].lastName || ''}`.trim() : undefined;
    const jobTitle = jobDetails?.jobTitleName;
    const companyName = partner?.name;
    const recruiterName = partnerUser?.name;
    const jobLocation = jobDetails?.locationInfo?.displayName || jobLocation1 || applicants?.[0]?.applicationLocation || t('job_location_undefined');
    const contractType = jobDetails?.contractType ? t(`enum_${jobDetails?.contractType}`) : t('contract_type');

    // If single applicant and we have values, replace directly first (for backward compatibility with old format)
    // This handles old placeholder formats like {{job_title}}, {{company_name}}, etc.
    if (!isMultipleApplicants && (candidateName || jobTitle || companyName || recruiterName || jobLocation || contractType)) {
        let processedTemplate = template;

        // Replace old format placeholders directly
        if (jobTitle) {
            processedTemplate = processedTemplate.replace(/\{\{job_title\}\}/gi, jobTitle);
            processedTemplate = processedTemplate.replace(/\{JOB_TITLE\}/g, jobTitle);
        }
        if (companyName) {
            processedTemplate = processedTemplate.replace(/\{\{company_name\}\}/gi, companyName);
            processedTemplate = processedTemplate.replace(/\{COMPANY_NAME\}/g, companyName);
        }
        if (recruiterName) {
            processedTemplate = processedTemplate.replace(/\{\{recruiter_name\}\}/gi, recruiterName);
            processedTemplate = processedTemplate.replace(/\{RECRUITER_NAME\}/g, recruiterName);
        }
        if (candidateName) {
            processedTemplate = processedTemplate.replace(/\{\{candidate_name\}\}/gi, candidateName);
            processedTemplate = processedTemplate.replace(/\{\{first_name\}\}/gi, applicants?.[0]?.firstName || '');
            processedTemplate = processedTemplate.replace(/\{\{last_name\}\}/gi, applicants?.[0]?.lastName || '');
            processedTemplate = processedTemplate.replace(/\{\{full_name\}\}/gi, candidateName);
            processedTemplate = processedTemplate.replace(/\{CANDIDATE_NAME\}/g, candidateName);
        }
        if (jobLocation) {
            processedTemplate = processedTemplate.replace(/\{\{job_location\}\}/gi, jobLocation);
            processedTemplate = processedTemplate.replace(/\{JOB_LOCATION\}/g, jobLocation);
        }
        if (contractType) {
            processedTemplate = processedTemplate.replace(/\{\{contract_type\}\}/gi, contractType);
            processedTemplate = processedTemplate.replace(/\{CONTRACT_TYPE\}/g, contractType);
        }

        // Then use the new function to handle any remaining placeholders and language conversion
        return replaceTemplatePlaceholders(
            processedTemplate,
            lang,
            isMultipleApplicants,
            {
                candidateName,
                jobTitle,
                companyName,
                recruiterName,
                jobLocation,
                contractType,
            }
        );
    }

    // Use the new function for all cases
    return replaceTemplatePlaceholders(
        template,
        lang,
        isMultipleApplicants,
        {
            candidateName,
            jobTitle,
            companyName,
            recruiterName,
            jobLocation,
            contractType,
        }
    );
}

export const replaceVariablesToArabic = (text: string, lang: string) => {
    if (!text || lang !== 'ar') return text;

    // Use case-insensitive regex to match placeholders with any capitalization
    return text.replace(/\{\{job_title\}\}/gi, '{{المسمى_الوظيفي}}')
        .replace(/\{\{company_name\}\}/gi, '{{اسم_الشركة}}')
        .replace(/\{\{recruiter_name\}\}/gi, '{{اسم_الموظف}}')
        .replace(/\{\{first_name\}\}/gi, '{{اسم_المرشح}}')
        .replace(/\{\{last_name\}\}/gi, '{{اسم_المرشح}}')
        .replace(/\{\{full_name\}\}/gi, '{{اسم_المرشح}}')
        .replace(/\{\{candidate_name\}\}/gi, '{{اسم_المرشح}}')
        .replace(/\{\{job_location\}\}/gi, '{{موقع_الوظيفة}}')
        .replace(/\{\{contract_type\}\}/gi, '{{نوع_العقد}}')
        .replace(/\{\{interview_invitation_link\}\}/gi, '{{رابط_المقابلة}}')
        .replace(/\{\{interview_invitation_selected_slot\}\}/gi, '{{موعد_المقابلة}}');
}

/**
 * Extracts buttons from a WhatsApp template component
 * Supports both array format and object format
 * Handles BUTTONS component type with nested buttons array
 */
export const extractTemplateButtons = (template: WhatsAppTemplate): TemplateButton[] => {
    if (!template?.components) return [];

    let buttons: TemplateButton[] = [];

    // Handle array format: [{type: "BODY", text: "..."}, {type: "BUTTONS", buttons: [...]}, ...]
    if (Array.isArray(template.components)) {
        // First, check for BUTTONS component type (contains nested buttons array)
        const buttonsComponent = template.components.find((c: any) =>
            c?.type === 'BUTTONS' || c?.type === 'buttons'
        );

        if (buttonsComponent && Array.isArray(buttonsComponent.buttons)) {
            // Extract buttons from BUTTONS component
            buttons = buttonsComponent.buttons.map((btn: any): TemplateButton | null => {
                if (btn.type === 'url' || btn.type === 'URL') {
                    return {
                        type: 'url' as const,
                        text: btn.text || '',
                        url: btn.url || ''
                    };
                } else if (btn.type === 'flow' || btn.type === 'FLOW') {
                    return {
                        type: 'flow' as const,
                        text: btn.text || '',
                        flow_id: btn.flow_id || btn.flowId || '',
                        flow_action: btn.flow_action || btn.flowAction || ''
                    };
                } else if (btn.type === 'quick_reply' || btn.type === 'QUICK_REPLY') {
                    return {
                        type: 'quick_reply' as const,
                        text: btn.text || ''
                    };
                }
                return null;
            }).filter((btn): btn is TemplateButton => btn !== null);
        } else {
            // Fallback: check for individual BUTTON components
            const buttonComponents = template.components.filter((c: any) =>
                c?.type === 'BUTTON' || c?.type === 'button'
            );

            buttons = buttonComponents.map((btn: any): TemplateButton | null => {
                if (btn.sub_type === 'URL' || btn.sub_type === 'url') {
                    return {
                        type: 'url' as const,
                        text: btn.text || '',
                        url: btn.url || ''
                    };
                } else if (btn.sub_type === 'FLOW' || btn.sub_type === 'flow') {
                    return {
                        type: 'flow' as const,
                        text: btn.text || '',
                        flow_id: btn.flow_id || '',
                        flow_action: btn.flow_action || ''
                    };
                } else if (btn.sub_type === 'QUICK_REPLY' || btn.sub_type === 'quick_reply') {
                    return {
                        type: 'quick_reply' as const,
                        text: btn.text || ''
                    };
                }
                return null;
            }).filter((btn): btn is TemplateButton => btn !== null);
        }
    }
    // Handle object format: {body: {...}, buttons: [...]}
    else if (typeof template.components === 'object') {
        const buttonsArray = template.components.buttons || template.components.button || [];
        if (Array.isArray(buttonsArray)) {
            buttons = buttonsArray.map((btn: any): TemplateButton | null => {
                if (btn.type === 'url' || btn.type === 'URL') {
                    return {
                        type: 'url' as const,
                        text: btn.text || '',
                        url: btn.url || ''
                    };
                } else if (btn.type === 'flow' || btn.type === 'FLOW') {
                    return {
                        type: 'flow' as const,
                        text: btn.text || '',
                        flow_id: btn.flow_id || btn.flowId || '',
                        flow_action: btn.flow_action || btn.flowAction || ''
                    };
                } else if (btn.type === 'quick_reply' || btn.type === 'QUICK_REPLY') {
                    return {
                        type: 'quick_reply' as const,
                        text: btn.text || ''
                    };
                }
                return null;
            }).filter((btn): btn is TemplateButton => btn !== null);
        }
    }

    return buttons;
};